import { MutableRefObject, useEffect, useRef, useState } from 'react';

const useElementDimension = (
  elementRef: MutableRefObject<HTMLDivElement | null>,
  defaultWidth?: number,
  defaultHeight?: number,
) => {
  const [width, setWidth] = useState(defaultWidth || 0);
  const [height, setHeight] = useState(defaultHeight || 0);
  const observedElementRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // No deps: the element may mount later (e.g. after a loading state) or be
  // replaced, and a ref change does not trigger a re-render. Re-observe only
  // when the element actually changes.
  useEffect(() => {
    const element = elementRef.current;
    if (element === observedElementRef.current) return;

    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    observedElementRef.current = element;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      // setState bails out when the value is unchanged
      setWidth(element.offsetWidth);
      setHeight(element.offsetHeight);
    });
    resizeObserver.observe(element);
    resizeObserverRef.current = resizeObserver;
  });

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      observedElementRef.current = null;
    },
    [],
  );

  return { width, height };
};

export default useElementDimension;
